import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  composeSyncValidators,
  required,
  validEmail,
} from 'coupon-common/src/utils';

import ICON_MAIL from '../../assets/icons/icon-envelope.svg';
import ICON_KEY from '../../assets/icons/icon-key.svg';
import messages from './messages';

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="2.4" stroke="#8A8A8A" strokeWidth="1.4" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6c1.5 0 2.8.4 3.9 1M17 9s-1.1 2.2-3.1 3.8M9 15c-5 0-8-6-8-6a14.5 14.5 0 013-3.8"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 9a2 2 0 002.8 1.8M11 9a2 2 0 00-2.7-1.9" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M1 1l16 16" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.33A9 9 0 009 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.7A5.4 5.4 0 013.68 9c0-.59.1-1.16.27-1.7V4.97H.94A9 9 0 000 9c0 1.45.35 2.83.94 4.03l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 00.94 4.97l3.01 2.33C4.66 5.17 6.65 3.58 9 3.58z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        fill="#000000"
        d="M13.15 9.53c-.02-2.03 1.66-3.01 1.73-3.06-.95-1.38-2.42-1.57-2.95-1.6-1.26-.13-2.45.74-3.09.74-.64 0-1.62-.72-2.66-.7-1.37.02-2.63.79-3.33 2.01-1.42 2.46-.36 6.1 1.02 8.1.68.98 1.48 2.08 2.54 2.04 1.02-.04 1.4-.65 2.63-.65 1.22 0 1.57.65 2.65.63 1.1-.02 1.79-1 2.46-1.98a8.7 8.7 0 001.11-2.27 3.87 3.87 0 01-2.11-3.26z"
      />
      <path
        fill="#000000"
        d="M11.19 3.44c.56-.68.94-1.62.83-2.56-.81.03-1.79.54-2.37 1.21-.52.6-.98 1.56-.85 2.48.9.07 1.82-.45 2.39-1.13z"
      />
    </svg>
  );
}

const CustomerLoginForm = ({
  intl,
  submitting,
  handleSubmit,
  submitError,
  onGoogleSignIn,
  onAppleSignIn,
  socialError,
  socialLoading,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const anySocialLoading = Boolean(socialLoading);

  return (
    <form onSubmit={handleSubmit}>
      <button
        type="button"
        className="auth-btn auth-btn-social"
        onClick={onGoogleSignIn}
        disabled={anySocialLoading || submitting}
      >
        <GoogleIcon />
        {socialLoading === 'google' ? '...' : <FormattedMessage {...messages.continueWithGoogleButtonText} />}
      </button>

      <button
        type="button"
        className="auth-btn auth-btn-social"
        onClick={onAppleSignIn}
        disabled={anySocialLoading || submitting}
      >
        <AppleIcon />
        {socialLoading === 'apple' ? '...' : <FormattedMessage {...messages.continueWithAppleButtonText} />}
      </button>

      {socialError && <div className="auth-error">{socialError}</div>}

      <div className="auth-divider">
        <span><FormattedMessage {...messages.orDividerText} /></span>
      </div>

      <Field
        name="email"
        validate={composeSyncValidators(required, validEmail())}
      >
        {({ input, meta }) => (
          <label className="auth-field">
            <img src={ICON_MAIL} alt="" className="auth-field-icon" />
            <div>
              <div className="auth-field-label">
                {intl.formatMessage(messages.emailLabelText)}
              </div>
              <input
                {...input}
                type="email"
                placeholder="example@gmail.com"
                autoComplete="email"
              />
            </div>
            {meta.touched && meta.error && (
              <span className="auth-field-error">{meta.error}</span>
            )}
          </label>
        )}
      </Field>

      <Field name="password" validate={required}>
        {({ input, meta }) => (
          <label className="auth-field">
            <img src={ICON_KEY} alt="" className="auth-field-icon" />
            <div>
              <div className="auth-field-label">
                {intl.formatMessage(messages.passwordLabelText)}
              </div>
              <input
                {...input}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              type="button"
              className="auth-field-toggle"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
            {meta.touched && meta.error && (
              <span className="auth-field-error">{meta.error}</span>
            )}
          </label>
        )}
      </Field>

      {submitError && <div className="auth-error">{submitError}</div>}

      <div className="auth-row-between">
        <label className="auth-checkbox">
          <input type="checkbox" />
          <span className="auth-checkbox-box" />
          <FormattedMessage {...messages.rememberMeCheckboxText} />
        </label>
        <Link to="/customer-forgot-password" className="auth-link">
          <FormattedMessage {...messages.forgotPasswordLinkText} />
        </Link>
      </div>

      <button
        type="submit"
        className="auth-btn auth-btn-solid auth-btn-block"
        disabled={submitting}
      >
        {submitting ? '...' : <FormattedMessage {...messages.loginButtonText} />}
      </button>

      <p className="auth-footer-text">
        <FormattedMessage {...messages.goToSignUpLinkText} />{' '}
        <Link to="/customer-signup" className="auth-link">Register</Link>
      </p>
    </form>
  );
};

CustomerLoginForm.propTypes = {
  intl: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  submitError: PropTypes.string,
  onGoogleSignIn: PropTypes.func,
  onAppleSignIn: PropTypes.func,
  socialError: PropTypes.string,
  socialLoading: PropTypes.string,
};

export default injectIntl(CustomerLoginForm);